import { ClientCompanyInfo } from "@shared/schema";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, Phone, Globe, UserPlus, Building2, Search, Check, Link2Off } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InlineEditableField } from "@/components/InlineEditableField";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type RelationshipPanelProps = {
  companyInfo?: ClientCompanyInfo;
  clientId: number;
  editMode?: boolean;
  onDraftChange?: (updates: Partial<ClientCompanyInfo>) => void;
};

type DebouncedFn<T extends (...args: any[]) => unknown> = ((...args: Parameters<T>) => void) & {
  cancel: () => void;
};

const createDebouncedFn = <T extends (...args: any[]) => void | Promise<void>>(
  fn: T,
  delay: number,
): DebouncedFn<T> => {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const debounced = ((...args: Parameters<T>) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      timer = null;
      void fn(...args);
    }, delay);
  }) as DebouncedFn<T>;

  debounced.cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return debounced;
};

const RELATIONSHIP_TYPES = ['Family', 'Group', 'Company', 'Organization'] as const;

type RelationshipType = (typeof RELATIONSHIP_TYPES)[number];

type Relationship = {
  id: number;
  name: string;
  relationshipType: RelationshipType;
  industry?: string | null;
  website?: string | null;
  owner?: string | null;
  employees?: number | null;
  estimatedAnnualTravelSpend?: number | null;
  mailingAddress?: string | null;
  mailingCity?: string | null;
  mailingState?: string | null;
  mailingZipCode?: string | null;
  mailingCountry?: string | null;
  billingAddress?: string | null;
  billingCity?: string | null;
  billingState?: string | null;
  billingZipCode?: string | null;
  billingCountry?: string | null;
  primaryClientId?: number | null;
  billingClientId?: number | null;
};

type RelationshipMemberSummary = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  isPrimary?: boolean | null;
  isBilling?: boolean | null;
};

const normalizeRelationshipRecord = (value: unknown): Relationship | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  if (typeof record.id !== 'number' || typeof record.name !== 'string') {
    return null;
  }

  const relationshipTypeRaw =
    (record.relationshipType as RelationshipType | undefined) ??
    (record.relationship_type as RelationshipType | undefined) ??
    'Company';

  const getString = (primary: string, fallback?: string): string | null => {
    const primaryValue = record[primary];
    if (typeof primaryValue === 'string' && primaryValue.trim()) {
      return primaryValue;
    }
    if (fallback) {
      const fallbackValue = record[fallback];
      if (typeof fallbackValue === 'string' && fallbackValue.trim()) {
        return fallbackValue;
      }
    }
    return null;
  };

  const getNumber = (primary: string, fallback?: string): number | null => {
    const value = record[primary] ?? (fallback ? record[fallback] : undefined);
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  return {
    id: record.id,
    name: record.name,
    relationshipType: relationshipTypeRaw,
    industry: getString('industry'),
    website: getString('website'),
    owner: getString('owner'),
    employees: getNumber('employees'),
    estimatedAnnualTravelSpend: getNumber(
      'estimatedAnnualTravelSpend',
      'estimated_annual_travel_spend',
    ),
    mailingAddress: getString('mailingAddress', 'mailing_address'),
    mailingCity: getString('mailingCity', 'mailing_city'),
    mailingState: getString('mailingState', 'mailing_state'),
    mailingZipCode: getString('mailingZipCode', 'mailing_zip_code'),
    mailingCountry: getString('mailingCountry', 'mailing_country'),
    billingAddress: getString('billingAddress', 'billing_address'),
    billingCity: getString('billingCity', 'billing_city'),
    billingState: getString('billingState', 'billing_state'),
    billingZipCode: getString('billingZipCode', 'billing_zip_code'),
    billingCountry: getString('billingCountry', 'billing_country'),
    primaryClientId: getNumber('primaryClientId', 'primary_client_id'),
    billingClientId: getNumber('billingClientId', 'billing_client_id'),
  };
};

const toRelationshipArray = (input: unknown): Relationship[] => {
  if (Array.isArray(input)) {
    return input
      .map((item) => normalizeRelationshipRecord(item))
      .filter((item): item is Relationship => item !== null);
  }

  if (input && typeof input === 'object') {
    return Object.values(input as Record<string, unknown>)
      .map((item) => normalizeRelationshipRecord(item))
      .filter((item): item is Relationship => item !== null);
  }

  return [];
};

const normalizeRelationshipMember = (
  value: unknown,
): RelationshipMemberSummary | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Partial<RelationshipMemberSummary> & {
    firstName?: string | null;
    lastName?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  };

  if (typeof record.id !== 'number') {
    return null;
  }

  const resolveName = (
    primary: string | null | undefined,
    fallback: string | null | undefined,
  ): string | null => {
    const candidate = primary ?? fallback ?? null;
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    return null;
  };

  return {
    id: record.id,
    firstName: resolveName(record.firstName, record.first_name),
    lastName: resolveName(record.lastName, record.last_name),
    isPrimary: record.isPrimary ?? null,
    isBilling: record.isBilling ?? null,
  };
};

const toRelationshipMembers = (
  input: unknown,
): RelationshipMemberSummary[] => {
  if (Array.isArray(input)) {
    return input
      .map((item) => normalizeRelationshipMember(item))
      .filter((item): item is RelationshipMemberSummary => item !== null);
  }

  if (input && typeof input === 'object') {
    return Object.values(input as Record<string, unknown>)
      .map((item) => normalizeRelationshipMember(item))
      .filter((item): item is RelationshipMemberSummary => item !== null);
  }

  return [];
};

const formatRelationshipMemberName = (
  contact: RelationshipMemberSummary,
): string => {
  const first = contact.firstName?.trim() ?? '';
  const last = contact.lastName?.trim() ?? '';
  const combined = `${first} ${last}`.trim();
  return combined || 'Unknown contact';
};
const toNullableString = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
};

export function RelationshipPanel({ companyInfo, clientId, editMode = false, onDraftChange }: RelationshipPanelProps) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isRelationshipDialogOpen, setIsRelationshipDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const companyInfoId = companyInfo?.id ?? null;
  const [relationshipSearchTerm, setRelationshipSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Relationship[]>([]);
  const [selectedRelationship, setSelectedRelationship] =
    useState<Relationship | null>(null);
  const [selectedRelationshipType, setSelectedRelationshipType] =
    useState<RelationshipType>('Company');
  const [isSearching, setIsSearching] = useState(false);
  const [activeRelationshipTab, setActiveRelationshipTab] = useState<string | undefined>(undefined);
  const abortControllerRef = useRef<AbortController | null>(null);
  const searchSeqRef = useRef(0);
  const [showCreateNew, setShowCreateNew] = useState(false);
  
  const createMutation = useMutation({
    mutationFn: async () => {
      const created = await apiRequest('POST', '/api/client-company-info', { clientId });
      return created;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/company`] });
      toast({ title: 'Work info added' });
    },
    onError: () => toast({ title: 'Failed to add work info', variant: 'destructive' })
  });

  const patch = async (id: number, updates: Partial<ClientCompanyInfo>) => {
    await apiRequest('PATCH', `/api/client-company-info/${id}`, updates);
    await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/company`] });
  };

  const saveCompanyInfoField = useCallback(
    async (updates: Partial<ClientCompanyInfo>) => {
      if (companyInfoId == null) {
        return;
      }
      await patch(companyInfoId, updates);
    },
    [companyInfoId, patch],
  );

  // Create a stable debounced search function
  const searchRelationships = useMemo(
    () =>
      createDebouncedFn(async (rawSearchTerm: string) => {
        const trimmedTerm = rawSearchTerm.trim();
        if (trimmedTerm.length < 2) {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
          }
          searchSeqRef.current += 1;
          setSearchResults([]);
          setShowCreateNew(false);
          setIsSearching(false);
          return;
        }

        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;
        const mySeq = ++searchSeqRef.current;
        setIsSearching(true);

        try {
          const response = await fetch(`/api/relationships?search=${encodeURIComponent(trimmedTerm)}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            credentials: 'include',
            signal: controller.signal,
          });

          if (!response.ok) {
            return;
          }

          const raw = await response.json().catch(() => null);
          const results = toRelationshipArray(raw);

          if (mySeq === searchSeqRef.current) {
            setSearchResults(results);
            setShowCreateNew(results.length === 0 && trimmedTerm.length > 0);
          }
        } catch (error) {
          if ((error as { name?: string } | null)?.name !== 'AbortError') {
            console.error('Failed to search relationships:', error);
            if (mySeq === searchSeqRef.current) {
              setSearchResults([]);
              setShowCreateNew(false);
            }
          }
        } finally {
          if (mySeq === searchSeqRef.current) {
            setIsSearching(false);
            abortControllerRef.current = null;
          }
        }
      }, 300),
    [],
  );
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      searchRelationships.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchRelationships]);

  useEffect(() => {
    searchRelationships(relationshipSearchTerm);
  }, [relationshipSearchTerm, searchRelationships]);

  useEffect(() => {
    if (selectedRelationship) {
      setSelectedRelationshipType(selectedRelationship.relationshipType);
    }
  }, [selectedRelationship]);

  useEffect(() => {
    if (!isRelationshipDialogOpen) {
      setSelectedRelationship(null);
      setSelectedRelationshipType('Company');
      setRelationshipSearchTerm('');
      setShowCreateNew(false);
    }
  }, [isRelationshipDialogOpen]);

  // Primary relationship record (new)
  const { data: primaryRelationship, refetch: refetchRelationship } =
    useQuery<Relationship | null>({
      queryKey: ['/api/relationships/by-contact', clientId],
      queryFn: async () => {
        const response = await fetch(
          `/api/relationships/by-contact/${clientId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            credentials: 'include',
          },
        );
        if (!response.ok) {
          return null;
        }

        const payload = await response.json().catch(() => null);
        return normalizeRelationshipRecord(payload);
      },
    });

  // All relationships linked to this client
  const { data: relationshipsData } = useQuery<Relationship[]>({
    queryKey: ['/api/relationships/list-by-contact', clientId],
    queryFn: async () => {
      const response = await fetch(
        `/api/relationships/list-by-contact/${clientId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          credentials: 'include',
        },
      );
      if (!response.ok) {
        return [];
      }

      const payload = await response.json().catch(() => []);
      return toRelationshipArray(payload);
    },
  });

  const relationships = relationshipsData ?? [];

  useEffect(() => {
    if (relationships.length > 0 && !activeRelationshipTab) {
      setActiveRelationshipTab(String(relationships[0].id));
    }
  }, [activeRelationshipTab, relationships]);

  const activeRelationshipId = activeRelationshipTab
    ? Number.parseInt(activeRelationshipTab, 10)
    : primaryRelationship?.id ?? null;

  const activeRelationship =
    (typeof activeRelationshipId === 'number'
      ? relationships.find((rel) => rel.id === activeRelationshipId)
      : null) ?? primaryRelationship ?? null;

  const resolvedRelationshipId =
    typeof activeRelationshipId === 'number' ? activeRelationshipId : null;

  const { data: relationshipMembersData } = useQuery<
    RelationshipMemberSummary[]
  >({
    enabled: resolvedRelationshipId !== null,
    queryKey: ['/api/relationships', resolvedRelationshipId ?? 'none', 'clients'],
    queryFn: async () => {
      if (resolvedRelationshipId === null) {
        return [];
      }

      const response = await fetch(
        `/api/relationships/${resolvedRelationshipId}/clients`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        return [];
      }

      const payload = await response.json().catch(() => []);
      return toRelationshipMembers(payload);
    },
  });

  const relationshipMembers = relationshipMembersData ?? [];

  // Temporary aliases for legacy company-focused UI rendering
  const company = activeRelationship;
  const companies = relationships;
  const companyContacts = relationshipMembers;
  
  const connectRelationship = useMutation({
    mutationFn: async (relationshipData: {
      id?: number;
      name?: string;
      relationshipType?: RelationshipType;
    }) => {
      let relationshipId = relationshipData.id ?? null;

      // Create new relationship if needed
      if (relationshipId == null && relationshipData.name) {
        const createdResponse = await apiRequest<unknown>(
          'POST',
          '/api/relationships',
          {
            name: relationshipData.name,
            relationshipType:
              relationshipData.relationshipType ?? selectedRelationshipType,
          },
        );
        const normalized = normalizeRelationshipRecord(createdResponse);

        if (normalized) {
          relationshipId = normalized.id;
        } else if (createdResponse && typeof createdResponse === 'object') {
          const candidate = createdResponse as {
            id?: number | string;
            Id?: number | string;
            ID?: number | string;
          };
          const rawId = candidate.id ?? candidate.Id ?? candidate.ID;
          const numericId =
            typeof rawId === 'string' ? Number.parseInt(rawId, 10) : rawId;
          if (typeof numericId === 'number' && Number.isFinite(numericId)) {
            relationshipId = numericId;
          }
        }

        if (relationshipId == null) {
          throw new Error(
            'Relationship creation failed - response did not include an id',
          );
        }
      }

      if (relationshipId == null) {
        throw new Error('A relationship id is required to link a contact');
      }

      await apiRequest('POST', `/api/relationships/${relationshipId}/clients`, {
        clientId,
      });

      return relationshipId;
    },
    onSuccess: async (relationshipId: number) => {
      // Optimistically set the relationship in cache so UI reflects linkage immediately
      const fallbackName = relationshipSearchTerm.trim() || 'Relationship';

      const optimistic =
        normalizeRelationshipRecord({
          id: relationshipId,
          name: selectedRelationship?.name ?? fallbackName,
          relationshipType:
            selectedRelationship?.relationshipType ?? selectedRelationshipType,
          industry: selectedRelationship?.industry ?? null,
          website: selectedRelationship?.website ?? null,
        }) ?? {
          id: relationshipId,
          name: selectedRelationship?.name ?? fallbackName,
          relationshipType:
            selectedRelationship?.relationshipType ?? selectedRelationshipType,
        };

      queryClient.setQueryData(
        ['/api/relationships/by-contact', clientId],
        optimistic,
      );
      queryClient.setQueryData(
        ['/api/relationships/list-by-contact', clientId],
        (old: unknown) => {
          const existing = Array.isArray(old) ? (old as Relationship[]) : [];
          const next = existing.filter((rel) => rel.id !== relationshipId);
          next.unshift(optimistic as Relationship);
          return next;
        },
      );

      await refetchRelationship();
      await queryClient.invalidateQueries({
        queryKey: ['/api/relationships/by-contact', clientId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['/api/relationships/list-by-contact', clientId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['/api/relationships', relationshipId, 'clients'],
      });
      setRelationshipSearchTerm('');
      setSelectedRelationship(null);
      setIsRelationshipDialogOpen(false);
      toast({ title: 'Relationship linked successfully' });
    },
    onError: (error: any) => {
      console.error('Failed to link relationship:', error);
      toast({
        title: 'Failed to link relationship',
        description: error?.message || 'An error occurred',
        variant: 'destructive',
      });
    },
  });

  const updateRelationship = useMutation({
    mutationFn: async ({
      relationshipId,
      updates,
    }: {
      relationshipId: number;
      updates: Partial<Relationship>;
    }) => {
      return await apiRequest(
        'PATCH',
        `/api/relationships/${relationshipId}`,
        updates,
      );
    },
    onSuccess: async (_data, variables) => {
      await refetchRelationship();
      await queryClient.invalidateQueries({
        queryKey: ['/api/relationships/list-by-contact', clientId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['/api/relationships', variables.relationshipId, 'clients'],
      });
      toast({ title: 'Relationship updated' });
    },
    onError: () =>
      toast({
        title: 'Failed to update relationship',
        variant: 'destructive',
      }),
  });

  const removeLink = useMutation({
    mutationFn: async (relationshipId: number) => {
      const res = await fetch(
        `/api/relationships/${relationshipId}/clients/${clientId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          credentials: 'include',
        },
      );
      if (!res.ok) throw new Error('Failed to unlink relationship');
      return relationshipId;
    },
    onSuccess: async (removedRelationshipId: number) => {
      // Remove from list cache
      queryClient.setQueryData(
        ['/api/relationships/list-by-contact', clientId],
        (old: unknown) => {
          const arr = Array.isArray(old) ? (old as Relationship[]) : [];
          return arr.filter((rel) => rel.id !== removedRelationshipId);
        },
      );
      // Update active tab if needed
      setActiveRelationshipTab((prev) => {
        if (!prev || Number.parseInt(prev, 10) !== removedRelationshipId) {
          return prev;
        }
        const list =
          (queryClient.getQueryData([
            '/api/relationships/list-by-contact',
            clientId,
          ]) as Relationship[]) ?? [];
        return list.length ? String(list[0].id) : undefined;
      });
      // Update single relationship cache from list
      const listAfter =
        (queryClient.getQueryData([
          '/api/relationships/list-by-contact',
          clientId,
        ]) as Relationship[]) ?? [];
      queryClient.setQueryData(
        ['/api/relationships/by-contact', clientId],
        listAfter[0] || null,
      );
      await queryClient.invalidateQueries({
        queryKey: ['/api/relationships/by-contact', clientId],
      });
      toast({ title: 'Contact unlinked from relationship' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to unlink relationship',
        description: error?.message || 'An error occurred',
        variant: 'destructive',
      });
    },
  });

  return (
    <div className="space-y-6">
      {!company && !companyInfo ? (
        <div className="space-y-4">
          <Alert>
            <AlertDescription>
              No relationship has been linked to this contact yet.
            </AlertDescription>
          </Alert>
          
          <div className="flex gap-2">
            <Button onClick={() => setIsRelationshipDialogOpen(true)} className="gap-2">
              <Building2 className="h-4 w-4" />
              Link Relationship
            </Button>
            
            <Button variant="outline" onClick={() => setIsEditDialogOpen(true)}>
              Add Contact Work Info
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Relationships sub-tabs or single fallback */}
          {companies.length > 0 ? (
            <Tabs value={activeRelationshipTab} onValueChange={setActiveRelationshipTab}>
              <TabsList className="flex flex-wrap">
                {companies.map((c) => (
                  <TabsTrigger key={c.id} value={String(c.id)} className="text-sm">
                    {c.name}
                  </TabsTrigger>
                ))}
              </TabsList>
              {companies.map((c) => (
                <TabsContent key={c.id} value={String(c.id)}>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="space-y-6">
                        {/* Relationship Header */}
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-xl font-semibold flex items-center gap-2">
                              <Building2 className="h-5 w-5" />
                              {c.name}
                              <Badge variant="outline" className="text-xs">
                                {c.relationshipType}
                              </Badge>
                            </h3>
                            {c.industry && (
                              <p className="text-sm text-muted-foreground mt-1">{c.industry}</p>
                            )}
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setIsRelationshipDialogOpen(true)}
                          >
                            Edit Relationship
                          </Button>
                        </div>
                        

                        {/* Relationship Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <InlineEditableField
                            label="Industry"
                            value={c.industry}
                            placeholder="Technology, Finance, etc."
                            forceEdit={false}
                            onSave={async (val) => updateRelationship.mutate({ relationshipId: c.id, updates: { industry: val } })}
                          />
                          <InlineEditableField
                            label="Website"
                            value={c.website}
                            placeholder="https://example.com"
                            forceEdit={false}
                            onSave={async (val) => updateRelationship.mutate({ relationshipId: c.id, updates: { website: val } })}
                          />
                          <InlineEditableField
                            label="Owner"
                            value={c.owner}
                            placeholder="Account Owner"
                            forceEdit={false}
                            onSave={async (val) => updateRelationship.mutate({ relationshipId: c.id, updates: { owner: val } })}
                          />
                          <InlineEditableField
                            label="Employees"
                            value={c.employees?.toString()}
                            placeholder="Number of employees"
                            forceEdit={false}
                            onSave={async (val) => updateRelationship.mutate({ relationshipId: c.id, updates: { employees: val ? parseInt(val) : null } })}
                          />
                          <InlineEditableField
                            label="Annual Travel Spend"
                            value={c.estimatedAnnualTravelSpend?.toString()}
                            placeholder="Estimated annual spend"
                            forceEdit={false}
                            onSave={async (val) => updateRelationship.mutate({ relationshipId: c.id, updates: { estimatedAnnualTravelSpend: val ? parseInt(val) : null } })}
                          />
                        </div>

                        {/* Relationship Members */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">Relationship Members</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-[12px] font-medium text-[rgb(120,113,108)]">Primary Contact</Label>
                              <Select
                                value={c.primaryClientId ? c.primaryClientId.toString() : undefined}
                                onValueChange={(val) => updateRelationship.mutate({ relationshipId: c.id, updates: { primaryClientId: val === 'none' ? null : parseInt(val) } })}
                              >
                                <SelectTrigger className="h-9 w-full">
                                  <SelectValue placeholder="Select primary contact" />
                                </SelectTrigger>
                                  <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {companyContacts.map((cc) => (
                                    <SelectItem key={cc.id} value={cc.id.toString()}>
                                      {formatRelationshipMemberName(cc)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div>
                              <Label className="text-[12px] font-medium text-[rgb(120,113,108)]">Billing Contact</Label>
                              <Select
                                value={c.billingClientId ? c.billingClientId.toString() : undefined}
                                onValueChange={(val) => updateRelationship.mutate({ relationshipId: c.id, updates: { billingClientId: val === 'none' ? null : parseInt(val) } })}
                              >
                                <SelectTrigger className="h-9 w-full">
                                  <SelectValue placeholder="Select billing contact" />
                                </SelectTrigger>
                                  <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {companyContacts.map((cc) => (
                                    <SelectItem key={cc.id} value={cc.id.toString()}>
                                      {formatRelationshipMemberName(cc)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          {/* List of all company contacts */}
                          <div className="mt-3">
                            <p className="text-[12px] font-medium text-[rgb(120,113,108)] mb-2">All Contacts ({companyContacts.length})</p>
                            <div className="space-y-1">
                              {companyContacts.map((cc) => (
                                <div key={cc.id} className="text-sm text-muted-foreground flex items-center gap-2">
                                  <span>{formatRelationshipMemberName(cc)}</span>
                                  {cc.id === c.primaryClientId && (
                                    <Badge variant="secondary" className="text-xs">Primary</Badge>
                                  )}
                                  {cc.id === c.billingClientId && (
                                    <Badge variant="secondary" className="text-xs">Billing</Badge>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Unlink contact from this company (tab view) */}
                      <div className="pt-2">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm" className="gap-2">
                              <Link2Off className="h-4 w-4" />
                              Remove this contact from relationship
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove from {c.name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will unlink the contact from this relationship. The relationship record will remain.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => removeLink.mutate(c.id)}>
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          ) : company ? (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  {/* Relationship Header */}
                  <div className="flex items-start justify-between">
                  <div>
                      <h3 className="text-xl font-semibold flex items-center gap-2">
                        <Building2 className="h-5 w-5" />
                        {company.name}
                        <Badge variant="outline" className="text-xs">{company.relationshipType}</Badge>
                      </h3>
                      {company.industry && (
                        <p className="text-sm text-muted-foreground mt-1">{company.industry}</p>
                      )}
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setIsRelationshipDialogOpen(true)}
                    >
                      Edit Relationship
                    </Button>
                  </div>

                  {/* Relationship Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <InlineEditableField
                      label="Industry"
                      value={company.industry}
                      placeholder="Technology, Finance, etc."
                      forceEdit={false}
                      onSave={async (val) => updateRelationship.mutate({ relationshipId: company.id, updates: { industry: val } })}
                    />
                    <InlineEditableField
                      label="Website"
                      value={company.website}
                      placeholder="https://example.com"
                      forceEdit={false}
                      onSave={async (val) => updateRelationship.mutate({ relationshipId: company.id, updates: { website: val } })}
                    />
                    <InlineEditableField
                      label="Owner"
                      value={company.owner}
                      placeholder="Account Owner"
                      forceEdit={false}
                      onSave={async (val) => updateRelationship.mutate({ relationshipId: company.id, updates: { owner: val } })}
                    />
                    <InlineEditableField
                      label="Employees"
                      value={company.employees?.toString()}
                      placeholder="Number of employees"
                      forceEdit={false}
                      onSave={async (val) => updateRelationship.mutate({ relationshipId: company.id, updates: { employees: val ? parseInt(val) : null } })}
                    />
                    <InlineEditableField
                      label="Annual Travel Spend"
                      value={company.estimatedAnnualTravelSpend?.toString()}
                      placeholder="Estimated annual spend"
                      forceEdit={false}
                      onSave={async (val) => updateRelationship.mutate({ relationshipId: company.id, updates: { estimatedAnnualTravelSpend: val ? parseInt(val) : null } })}
                    />
                  </div>

                  {/* Relationship Members */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Relationship Members</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[12px] font-medium text-[rgb(120,113,108)]">Primary Contact</Label>
                        <Select
                          value={company.primaryClientId ? company.primaryClientId.toString() : undefined}
                          onValueChange={(val) => updateRelationship.mutate({ relationshipId: company.id, updates: { primaryClientId: val === 'none' ? null : parseInt(val) } })}
                        >
                          <SelectTrigger className="h-9 w-full">
                            <SelectValue placeholder="Select primary contact" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {companyContacts.map((cc) => (
                              <SelectItem key={cc.id} value={cc.id.toString()}>
                                {formatRelationshipMemberName(cc)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label className="text-[12px] font-medium text-[rgb(120,113,108)]">Billing Contact</Label>
                        <Select
                          value={company.billingClientId ? company.billingClientId.toString() : undefined}
                          onValueChange={(val) => updateRelationship.mutate({ relationshipId: company.id, updates: { billingClientId: val === 'none' ? null : parseInt(val) } })}
                        >
                          <SelectTrigger className="h-9 w-full">
                            <SelectValue placeholder="Select billing contact" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {companyContacts.map((cc) => (
                              <SelectItem key={cc.id} value={cc.id.toString()}>
                                {formatRelationshipMemberName(cc)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                  </div>
                </div>
                    
                    {/* List of all company contacts */}
                <div className="mt-3">
                    <p className="text-[12px] font-medium text-[rgb(120,113,108)] mb-2">All Contacts ({companyContacts.length})</p>
                    <div className="space-y-1">
                        {companyContacts.map((cc) => (
                          <div key={cc.id} className="text-sm text-muted-foreground flex items-center gap-2">
                            <span>{formatRelationshipMemberName(cc)}</span>
                            {cc.id === company.primaryClientId && (
                              <Badge variant="secondary" className="text-xs">Primary</Badge>
                            )}
                            {cc.id === company.billingClientId && (
                              <Badge variant="secondary" className="text-xs">Billing</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Unlink contact from this company (single-company view) */}
                  <div className="pt-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm" className="gap-2">
                          <Link2Off className="h-4 w-4" />
                          Remove this contact from relationship
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove from {company.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will unlink the contact from this relationship. The relationship record will remain.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeLink.mutate(company.id)}>
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}
          
          {/* Contact's Work Information (per-contact fields) */}
          {companyInfo && (
            <>
          <div className="flex justify-between items-start">
            <div className="space-y-2">
                  <h2 className="text-lg font-semibold">Contact Work Information</h2>
              {companyInfo.jobTitle && (
                <p className="text-muted-foreground">
                  {companyInfo.jobTitle}
                  {companyInfo.department ? ` • ${companyInfo.department}` : ""}
                </p>
              )}
            </div>
            
            <Button variant="outline" size="sm" onClick={() => setIsEditDialogOpen(true)}>
                  Edit Work Info
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <dl className="space-y-4">
                  <InlineEditableField
                    label="Job Title"
                    value={companyInfo.jobTitle}
                    placeholder="Job Title"
                    forceEdit={false}
                    onSave={async (val) => saveCompanyInfoField({ jobTitle: val })}
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ jobTitle: val })}
                  />
                  <InlineEditableField
                    label="Department"
                    value={companyInfo.department}
                    placeholder="Department"
                    forceEdit={false}
                    onSave={async (val) => saveCompanyInfoField({ department: val })}
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ department: val })}
                  />
                  <InlineEditableField
                    label="Work Email"
                    value={companyInfo.workEmail}
                    placeholder="work@example.com"
                    forceEdit={false}
                    onSave={async (val) => saveCompanyInfoField({ workEmail: val })}
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ workEmail: val })}
                  />
                  <InlineEditableField
                    label="Work Phone"
                    value={companyInfo.workPhone}
                    placeholder="(555) 555-5555"
                    forceEdit={false}
                    onSave={async (val) => saveCompanyInfoField({ workPhone: val })}
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ workPhone: val })}
                  />
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <dl className="space-y-4">
                  <InlineEditableField
                    label="Work Address"
                    value={companyInfo.workAddress}
                    placeholder="Address"
                    forceEdit={false}
                    onSave={async (val) => saveCompanyInfoField({ workAddress: val })}
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ workAddress: val })}
                  />
                  <InlineEditableField
                    label="Work City"
                    value={companyInfo.workCity}
                    placeholder="City"
                    forceEdit={false}
                    onSave={async (val) => saveCompanyInfoField({ workCity: val })}
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ workCity: val })}
                  />
                  <InlineEditableField
                    label="Work State/Province"
                    value={companyInfo.workState}
                    placeholder="State/Province"
                    forceEdit={false}
                    onSave={async (val) => saveCompanyInfoField({ workState: val })}
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ workState: val })}
                  />
                  <InlineEditableField
                    label="Work Postal Code"
                    value={companyInfo.workZipCode}
                    placeholder="Postal Code"
                    forceEdit={false}
                    onSave={async (val) => saveCompanyInfoField({ workZipCode: val })}
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ workZipCode: val })}
                  />
                  <InlineEditableField
                    label="Work Country"
                    value={companyInfo.workCountry}
                    placeholder="Country"
                    forceEdit={false}
                    onSave={async (val) => saveCompanyInfoField({ workCountry: val })}
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ workCountry: val })}
                  />
                  <InlineEditableField
                    label="LinkedIn"
                    value={companyInfo.linkedIn}
                    placeholder="https://linkedin.com/..."
                    forceEdit={false}
                    onSave={async (val) => saveCompanyInfoField({ linkedIn: val })}
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.({ linkedIn: val })}
                  />
                </dl>
              </CardContent>
            </Card>
          </div>
        </>
      )}
        </>
      )}

      {/* Relationship Search/Link Dialog */}
      <Dialog open={isRelationshipDialogOpen} onOpenChange={setIsRelationshipDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {company ? "Edit Relationship Details" : "Link Relationship"}
            </DialogTitle>
            <DialogDescription>
              {company ? "Update the relationship information and addresses below." : "Search for an existing relationship or create a new one to link to this contact."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {!company ? (
              <>
                <div className="space-y-2">
                  <Label>Search for existing relationship or create new</Label>
                  <Input
                    placeholder="Type relationship name to search..."
                    value={relationshipSearchTerm}
                    onChange={(e) => setRelationshipSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Relationship Type</Label>
                  <Select
                    value={selectedRelationshipType}
                    onValueChange={(val) =>
                      setSelectedRelationshipType(val as RelationshipType)
                    }
                  >
                    <SelectTrigger className="h-9 w-full">
                      <SelectValue placeholder="Select relationship type" />
                    </SelectTrigger>
                    <SelectContent>
                      {RELATIONSHIP_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {isSearching && (
                  <div className="text-sm text-muted-foreground">Searching...</div>
                )}
                
                {searchResults.length > 0 && (
                  <div className="space-y-2">
                    <Label>Select a relationship:</Label>
                    <div className="border rounded-md divide-y max-h-60 overflow-y-auto">
                      {searchResults.map((comp) => (
                        <div
                          key={comp.id}
                          className={`p-3 cursor-pointer hover:bg-gray-50 ${
                            selectedRelationship?.id === comp.id
                              ? 'bg-blue-50'
                              : ''
                          }`}
                          onClick={() => {
                            setSelectedRelationship(comp);
                            setSelectedRelationshipType(comp.relationshipType);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="font-medium">{comp.name}</div>
                            <Badge variant="outline" className="text-xs">
                              {comp.relationshipType}
                            </Badge>
                          </div>
                          {comp.industry && (
                            <div className="text-sm text-muted-foreground">
                              {comp.industry}
                            </div>
                          )}
                          {comp.website && (
                            <div className="text-xs text-muted-foreground">
                              {comp.website}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {showCreateNew && relationshipSearchTerm.length > 0 && (
                  <Alert>
                    <AlertDescription>
                      No relationships found. Click "Create & Link" to create a new relationship named "{relationshipSearchTerm}".
                    </AlertDescription>
                  </Alert>
                )}
              </>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Edit relationship details using the inline fields above, or manage relationship addresses below.
                </p>
                
                {/* Relationship Addresses */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Mailing Address</h4>
                    <div className="space-y-3">
                      <InlineEditableField
                        label="Street"
                        value={company.mailingAddress}
                        placeholder="123 Main St"
                        forceEdit={false}
                        onSave={async (val) =>
                          updateRelationship.mutate({
                            relationshipId: company.id,
                            updates: { mailingAddress: toNullableString(val) },
                          })
                        }
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <InlineEditableField
                          label="City"
                          value={company.mailingCity}
                          placeholder="City"
                          forceEdit={false}
                          onSave={async (val) =>
                            updateRelationship.mutate({
                              relationshipId: company.id,
                              updates: { mailingCity: toNullableString(val) },
                            })
                          }
                        />
                        <InlineEditableField
                          label="State"
                          value={company.mailingState}
                          placeholder="State"
                          forceEdit={false}
                          onSave={async (val) =>
                            updateRelationship.mutate({
                              relationshipId: company.id,
                              updates: { mailingState: toNullableString(val) },
                            })
                          }
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <InlineEditableField
                          label="Zip"
                          value={company.mailingZipCode}
                          placeholder="Zip Code"
                          forceEdit={false}
                          onSave={async (val) =>
                            updateRelationship.mutate({
                              relationshipId: company.id,
                              updates: { mailingZipCode: toNullableString(val) },
                            })
                          }
                        />
                        <InlineEditableField
                          label="Country"
                          value={company.mailingCountry}
                          placeholder="Country"
                          forceEdit={false}
                          onSave={async (val) =>
                            updateRelationship.mutate({
                              relationshipId: company.id,
                              updates: { mailingCountry: toNullableString(val) },
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Billing Address</h4>
                    <div className="space-y-3">
                      <InlineEditableField
                        label="Street"
                        value={company.billingAddress}
                        placeholder="123 Main St"
                        forceEdit={false}
                        onSave={async (val) =>
                          updateRelationship.mutate({
                            relationshipId: company.id,
                            updates: { billingAddress: toNullableString(val) },
                          })
                        }
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <InlineEditableField
                          label="City"
                          value={company.billingCity}
                          placeholder="City"
                          forceEdit={false}
                          onSave={async (val) =>
                            updateRelationship.mutate({
                              relationshipId: company.id,
                              updates: { billingCity: toNullableString(val) },
                            })
                          }
                        />
                        <InlineEditableField
                          label="State"
                          value={company.billingState}
                          placeholder="State"
                          forceEdit={false}
                          onSave={async (val) =>
                            updateRelationship.mutate({
                              relationshipId: company.id,
                              updates: { billingState: toNullableString(val) },
                            })
                          }
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <InlineEditableField
                          label="Zip"
                          value={company.billingZipCode}
                          placeholder="Zip Code"
                          forceEdit={false}
                          onSave={async (val) =>
                            updateRelationship.mutate({
                              relationshipId: company.id,
                              updates: { billingZipCode: toNullableString(val) },
                            })
                          }
                        />
                        <InlineEditableField
                          label="Country"
                          value={company.billingCountry}
                          placeholder="Country"
                          forceEdit={false}
                          onSave={async (val) =>
                            updateRelationship.mutate({
                              relationshipId: company.id,
                              updates: { billingCountry: toNullableString(val) },
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            {!company ? (
              <>
                <Button variant="outline" onClick={() => setIsRelationshipDialogOpen(false)}>
                  Cancel
                </Button>
                {selectedRelationship ? (
                  <Button
                    onClick={() =>
                      connectRelationship.mutate({
                        id: selectedRelationship.id,
                      })
                    }
                    disabled={connectRelationship.isPending}
                  >
                    {connectRelationship.isPending
                      ? 'Linking...'
                      : 'Link Selected Relationship'}
                  </Button>
                ) : showCreateNew && relationshipSearchTerm.trim() ? (
                  <Button
                    onClick={() =>
                      connectRelationship.mutate({
                        name: relationshipSearchTerm.trim(),
                        relationshipType: selectedRelationshipType,
                      })
                    }
                    disabled={connectRelationship.isPending}
                  >
                    {connectRelationship.isPending
                      ? 'Creating...'
                      : 'Create & Link New Relationship'}
                  </Button>
                ) : null}
              </>
            ) : (
              <Button onClick={() => setIsRelationshipDialogOpen(false)}>
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Contact Work Info Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {companyInfo ? "Edit Contact Work Information" : "Add Contact Work Information"}
            </DialogTitle>
            <DialogDescription>
              {companyInfo ? "Edit the contact's work-related information." : "Add work information specific to this contact."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {!companyInfo ? (
              <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating…' : 'Create Work Info'}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">Use inline fields to edit contact's work information.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
